import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage, apiErrorType } from "@/lib/api-error";
import type { components } from "@dolfin/api-public";
import { DeliveryStateBadge, IN_FLIGHT } from "@/components/EInvoiceDeliveryState";
import { networkLabel } from "@/components/SendPlanSummary";

type Delivery = components["schemas"]["OutboundEInvoiceView"];

const POLL_MS = 3000;
const POLL_TIMEOUT_MS = 120_000;

/** The backend's answer for a credit note that has not been sent electronically. */
const DELIVERY_NOT_FOUND_TYPE = "EInvoicing.DeliveryNotFound";

/** Past this, a missing delivery means never e-invoiced (or awaiting SDI's verdict, far longer than a page stays open). */
const JUST_ISSUED_MS = 60_000;

interface Props {
  invoiceId: string;
  creditNoteId: string;
  /** When the credit note was issued, so a just-issued note waits briefly for its delivery to appear. */
  issuedAt?: string | null;
  /** Whether the client holds the e-invoicing module; without it no request is made. */
  enabled: boolean;
  canRetry: boolean;
  /** Null while unknown. Without a registration there is nothing to send as, so send and resend are disabled. */
  registered?: boolean | null;
  /** Told the delivery each time it is read (null when there is none), so a page can show its details. */
  onDelivery?: (delivery: Delivery | null) => void;
}

/** Sent automatically over the invoice's network once issued; renders nothing when there is no delivery. */
export type CreditNoteDelivery = Delivery;

export function CreditNoteEInvoiceStatus({
  invoiceId,
  creditNoteId,
  issuedAt,
  enabled,
  canRetry,
  registered = true,
  onDelivery,
}: Props) {
  const api = useDolfinClient();
  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [retrying, setRetrying] = useState(false);
  // Only when the credit note has no delivery: whether its invoice's e-invoice is issued, i.e. whether
  // an explicit send could work. Keeps the Send button off credit notes on invoices never e-invoiced.
  const [sendable, setSendable] = useState(false);
  // Bumped after a retry to start a fresh polling window: the previous one ended when the delivery failed.
  const [pollRun, setPollRun] = useState(0);

  /** "Not found" clears the badge; other failures keep the last state. `undefined` means the read failed. */
  const load = useCallback(async (): Promise<Delivery | null | undefined> => {
    const { data, error } = await api.GET("/v1/invoices/{invoiceId}/credit-notes/{creditNoteId}/e-invoice", {
      params: { path: { invoiceId, creditNoteId } },
    });
    if (error) {
      if (apiErrorType(error).includes(DELIVERY_NOT_FOUND_TYPE)) {
        setDelivery(null);
        onDelivery?.(null);
        return null;
      }
      return undefined;
    }
    setDelivery(data ?? null);
    onDelivery?.(data ?? null);
    return data ?? null;
  }, [api, invoiceId, creditNoteId, onDelivery]);

  useEffect(() => {
    if (!enabled || delivery) {
      setSendable(false);
      return;
    }
    let cancelled = false;
    void api
      .GET("/v1/invoices/{id}/e-invoice", { params: { path: { id: invoiceId } } })
      .then(({ data }) => {
        if (!cancelled) setSendable(data?.state === "Delivered" || data?.state === "MadeAvailable");
      });
    return () => {
      cancelled = true;
    };
  }, [api, enabled, delivery, invoiceId]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const started = Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const justIssued = issuedAt != null && Date.now() - new Date(issuedAt).getTime() < JUST_ISSUED_MS;

    // Poll while the delivery is in flight. A missing one is polled only right after issue (or a retry),
    // when it is about to appear - not for every old credit note on an invoice that was never e-invoiced.
    const tick = async () => {
      const current = await load();
      if (cancelled || Date.now() - started > POLL_TIMEOUT_MS) return;
      const inFlight = current?.state != null && IN_FLIGHT.includes(current.state);
      const awaiting = current === null && (justIssued || pollRun > 0);
      if (inFlight || awaiting || current === undefined) timer = setTimeout(tick, POLL_MS);
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, load, issuedAt, pollRun]);

  async function handleSendOrResend(action: "send" | "resend") {
    setRetrying(true);
    try {
      const { error } =
        action === "send"
          ? await api.POST("/v1/invoices/{invoiceId}/credit-notes/{creditNoteId}/e-invoice/send", {
              params: { path: { invoiceId, creditNoteId } },
            })
          : await api.POST("/v1/invoices/{invoiceId}/credit-notes/{creditNoteId}/e-invoice/resend", {
              params: { path: { invoiceId, creditNoteId } },
            });
      if (error) {
        toast.error(apiErrorMessage(error, `Failed to ${action} the credit note's e-invoice.`));
        return;
      }
      setPollRun((n) => n + 1);
    } finally {
      setRetrying(false);
    }
  }

  if (!enabled) return null;

  const notRegistered = registered === false;
  const notRegisteredHint = "This organisation is not registered for e-invoicing, so it cannot send over the network.";

  if (!delivery) {
    // Nothing sent yet although the invoice went out electronically: the automatic send was refused
    // (e.g. the customer's routing code was missing). Once the data is fixed, send it from here.
    if (!sendable || !canRetry || registered === null) return null;
    const button = (
      <Button
        size="sm"
        variant="outline"
        className="h-6 gap-1 px-2 text-xs"
        onClick={() => handleSendOrResend("send")}
        disabled={retrying || notRegistered}
      >
        <Send className="size-3" />
        Send e-invoice
      </Button>
    );
    // A disabled button swallows the pointer events its tooltip needs, so the span carries them.
    return notRegistered ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{button}</span>
        </TooltipTrigger>
        <TooltipContent>{notRegisteredHint}</TooltipContent>
      </Tooltip>
    ) : (
      button
    );
  }

  const faults = (delivery.faults ?? []).map((f) => f.message).filter(Boolean).join(" ");

  return (
    <span className="inline-flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <DeliveryStateBadge state={delivery.state} />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {faults || `E-invoice via ${networkLabel(delivery.network)}${delivery.sandbox ? " (test)" : ""}`}
        </TooltipContent>
      </Tooltip>
      {/* Failed or abandoned and worth sending again: rebuilt from current data when nothing reached the
          network (the reason is in the tooltip; fix it first), otherwise the same document again. */}
      {delivery.canResend && canRetry && registered !== null && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                size="sm"
                variant="outline"
                className="h-6 gap-1 px-2 text-xs"
                aria-label="Resend credit note e-invoice"
                onClick={() => handleSendOrResend("resend")}
                disabled={retrying || notRegistered}
              >
                <RefreshCw className="size-3" />
                Resend
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {notRegistered
              ? notRegisteredHint
              : delivery.resendRebuildsDocument
                ? "Rebuilt from the current customer and organisation details. Fix them first."
                : "The customer may already have it, so the same document is sent again."}
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}
